import type {Deck, DeckLocation} from './model';
import {parseDeck} from './parser';

type SessionChange = {
  deck: Deck;
  location: DeckLocation;
  sourceChanged: boolean;
};

export class DeckSession extends EventTarget {
  #source: string;
  #deck: Deck;
  #location: DeckLocation;

  constructor(source: string, location: Partial<DeckLocation> = {}) {
    super();
    this.#source = source;
    this.#deck = parseDeck(source);
    this.#location = this.clamp({
      page: location.page ?? 1,
      step: location.step ?? 0,
    });
  }

  get source(): string {
    return this.#source;
  }

  get deck(): Deck {
    return this.#deck;
  }

  get location(): DeckLocation {
    return {...this.#location};
  }

  updateSource(source: string, selectedPage?: number): void {
    const deck = parseDeck(source);
    this.#source = source;
    this.#deck = deck;
    this.#location = this.clamp({
      page: selectedPage ?? this.#location.page,
      step: this.#location.step,
    });
    this.emit(true);
  }

  go(page: number, step = 0): void {
    const next = this.clamp({page, step});
    if (next.page === this.#location.page && next.step === this.#location.step) return;
    this.#location = next;
    this.emit(false);
  }

  forward(): void {
    const slide = this.#deck.slides[this.#location.page - 1];
    if (this.#location.step < (slide?.revealCount ?? 0)) {
      this.go(this.#location.page, this.#location.step + 1);
    } else {
      this.go(this.#location.page + 1, 0);
    }
  }

  backward(): void {
    if (this.#location.step > 0) {
      this.go(this.#location.page, this.#location.step - 1);
      return;
    }
    if (this.#location.page === 1) return;
    const page = Math.max(1, this.#location.page - 1);
    const previous = this.#deck.slides[page - 1];
    this.go(page, previous?.revealCount ?? 0);
  }

  onChange(listener: (change: SessionChange) => void): () => void {
    const handler = (event: Event) => listener((event as CustomEvent<SessionChange>).detail);
    this.addEventListener('change', handler);
    return () => this.removeEventListener('change', handler);
  }

  private clamp(location: DeckLocation): DeckLocation {
    const page = Math.min(Math.max(1, Number.isFinite(location.page) ? Math.floor(location.page) : 1), Math.max(1, this.#deck.slides.length));
    const count = this.#deck.slides[page - 1]?.revealCount ?? 0;
    return {page, step: Math.min(Math.max(0, Number.isFinite(location.step) ? Math.floor(location.step) : 0), count)};
  }

  private emit(sourceChanged: boolean): void {
    this.dispatchEvent(
      new CustomEvent<SessionChange>('change', {
        detail: {
          deck: this.#deck,
          location: this.location,
          sourceChanged,
        },
      }),
    );
  }
}
